import { forwardRef } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@core/utils/cn';

export type BackLinkProps = LinkProps;

/**
 * The header's "back" affordance, shared so every screen retreats the same way.
 *
 * A quiet pill rather than body text, so it reads as a control: on hover the
 * border and label lift out of muted and the arrow nudges toward the edge it
 * points at — the direction you are about to travel. Under RTL the arrow flips
 * and the nudge mirrors with it (the house pattern, see motion-button.tsx), so
 * "back" always leads the way the reader expects.
 *
 * Layout-neutral: pass spacing or positioning (`mb-4`, `absolute …`) via
 * className; tailwind-merge lets a passed token override a default one.
 */
export const BackLink = forwardRef<HTMLAnchorElement, BackLinkProps>(function BackLink(
  { className, children, ...props },
  ref,
) {
  return (
    <Link
      ref={ref}
      className={cn(
        'group inline-flex max-w-full items-center gap-1.5 rounded-full border border-line',
        'bg-card/50 py-1.5 pe-3.5 ps-2.5 text-sm font-medium text-muted backdrop-blur-sm',
        'transition-[color,border-color,background-color] duration-200',
        'hover:border-muted hover:bg-card hover:text-text',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        className,
      )}
      {...props}
    >
      <ArrowLeft
        size={16}
        aria-hidden="true"
        className={cn(
          'shrink-0 transition-transform duration-200 rtl:rotate-180',
          'group-hover:-translate-x-0.5 rtl:group-hover:translate-x-0.5',
        )}
      />
      <span className="min-w-0 truncate">{children}</span>
    </Link>
  );
});
